import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, HStack, Stack, Button, useToast } from "@chakra-ui/react";
import { getArchiveData, updateArchiveData } from "../../../services/Archive";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import FormLoader from "../../../CommonComponents/FormLoader";
import { useSelector } from "react-redux";
import { handleApiError, showSuccessToast } from "../../../util/errorHandler";

// Renders nothing; keeps the field value in formData (ui:widget "hidden" does not apply to array fields)
const HiddenField = () => null;

const Section5 = () => {
  const { alias } = useParams();
  const [quickSave, setQuickSave] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector((state) => state.preview.isEnabled);
  const toast = useToast();

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "_id",
      "agraphy_section_or_sub_section",
      "agraphy_specific_section",
      "type_of_creator",
      "type_of_joint_creator",
      "country_or_state_of_artwork_creation",
      "location_masterlist",
      "list_of_artists",
      "list_of_artists_alias",
      "highlight",
      "accession_number",
      "personality_or_film_or_artwork_on_front_cover",
      "personality_or_film_or_artwork_on_front_cover_alias",
      "publisher",
      "publisher_alias",
      "object_alias",
      "theme",
      "contributors1_alias",
      "contributors1",
      "contributors1_type",
      "contributors2_alias",
      "contributors2",
      "contributors2_type",
      "language_data",
      "citation_quotation",
      "quotations_and_other_text",
      "age",
    ];
    const rightFields = [
      "set_of",
      "copyright",
      "copyright_of_image",
      "insight_number",
      "insight_text_note1",
      "insight_text_note2",
      "insight_text_hyperlink",
      "facial_recognition_derived_title_of_artwork",
      "essential_reading",
      "suggested_reading",
      "is_source",
      "search_text",
      "extended_tags",
      "rec_aliases",
    ];

    const leftData = {};
    const rightData = {};

    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });

    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });

    return { leftData, rightData };
  };

  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  const hasValue = (value) => !!(value && String(value).trim() !== "");

  // Dependent fields are shown only when their parent field has a value
  const DEPENDENT_FIELDS = {
    citation_quotation: ["quotations_and_other_text"],
    contributors1_alias: ["contributors1", "contributors1_type"],
    contributors2_alias: ["contributors2", "contributors2_type"],
  };

  // Clear dependent fields when their parent is cleared, so hidden stale values are not saved
  const syncDependentFields = (next, prev) => {
    Object.entries(DEPENDENT_FIELDS).forEach(([parent, dependents]) => {
      if (hasValue(prev?.[parent]) && !hasValue(next[parent])) {
        dependents.forEach((field) => {
          next[field] = "";
        });
      }
    });
    return next;
  };

  // ui:widget override that hides a dependent field while its parent is empty
  const showWhen = (formData, parent) =>
    hasValue(formData?.[parent]) ? {} : { "ui:widget": "hidden" };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(syncDependentFields({ ...data.formData }, leftFormData));
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(
      syncDependentFields({ ...data.formData }, combinedFormData),
    );
  };

  // Left column schema
  const leftColumnSchema = {
    title: "",
    type: "object",
    properties: {
      _id: { type: "string" },
      agraphy_section_or_sub_section: {
        type: "string",
        title: "Agraphy Section or Sub-section",
      },
      agraphy_specific_section: {
        type: "string",
        title: "Agraphy Specific Section",
      },
      type_of_joint_creator: {
        type: "string",
        title: "Type of Joint Creator",
      },
      type_of_creator: {
        type: "string",
        title: "Type of Creator",
      },
      country_or_state_of_artwork_creation: {
        type: "string",
        title: "Country or State of Artwork Creation",
      },
      location_masterlist: {
        type: "string",
        title: "Location Masterlist",
      },
      list_of_artists: {
        type: "string",
        title: "List of Artists",
      },
      list_of_artists_alias: {
        type: "string",
        title: "List of Artists Alias",
      },
      highlight: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Highlight",
      },
      accession_number: {
        type: "string",
        title: "Accession Number",
      },
      personality_or_film_or_artwork_on_front_cover: {
        type: "string",
        title: "Personality, Film, or Artwork on Front Cover",
      },
      personality_or_film_or_artwork_on_front_cover_alias: {
        type: "string",
        title: "Personality, Film, or Artwork on Front Cover Alias",
      },
      publisher: {
        type: "string",
        title: "Publisher",
      },
      publisher_alias: {
        type: "string",
        title: "Publisher Alias",
      },
      object_alias: {
        type: "string",
        title: "Object Alias",
      },
      theme: {
        type: "string",
        title: "Theme",
      },
      contributors1_alias: {
        type: "string",
        title: "Contributors 1 Alias",
      },
      contributors1: {
        type: "string",
        title: "Contributors 1",
      },
      contributors1_type: {
        type: "string",
        title: "Contributors 1 Type",
      },
      contributors2_alias: {
        type: "string",
        title: "Contributors 2 Alias",
      },
      contributors2: {
        type: "string",
        title: "Contributors 2",
      },
      contributors2_type: {
        type: "string",
        title: "Contributors 2 Type",
      },
      language_data: {
        type: "string",
        title: "Language Data",
      },
      citation_quotation: {
        type: "string",
        title: "Citation Quotation",
      },
      quotations_and_other_text: {
        type: "string",
        title: "Quotations and Other Text",
      },
      age: {
        type: "string",
        title: "Age",
      },
    },
  };

  // Right column schema
  const rightColumnSchema = {
    title: "",
    type: "object",
    properties: {
      set_of: {
        type: "string",
        title: "Set Of",
      },
      copyright: {
        type: "string",
        title: "Copyright",
      },
      copyright_of_image: {
        type: "string",
        title: "Copyright of Image",
      },
      insight_number: {
        type: "string",
        title: "Insight Number",
      },
      insight_text_note1: {
        type: "string",
        title: "Insight Text Note 1",
      },
      insight_text_note2: {
        type: "string",
        title: "Insight Text Note 2",
      },
      insight_text_hyperlink: {
        type: "string",
        title: "Insight Text Hyperlink",
      },
      facial_recognition_derived_title_of_artwork: {
        type: "string",
        title: "Facial Recognition Derived Title of Artwork",
      },
      essential_reading: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Essential Reading",
      },
      suggested_reading: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Suggested Reading",
      },
      is_source: {
        type: "string",
        title: "Is Source",
        default: "",
      },
      search_text: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Search Text",
      },
      extended_tags: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Extended Tags",
      },
      rec_aliases: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Rec Aliases",
      },
    },
  };

  // Combined schema for preview mode
  const combinedSchema = {
    title: "Quadrant-5 fields",
    description: "",
    type: "object",
    properties: {
      _id: { type: "string" },
      agraphy_section_or_sub_section: {
        type: "string",
        title: "Agraphy Section or Sub-section",
      },
      agraphy_specific_section: {
        type: "string",
        title: "Agraphy Specific Section",
      },
      type_of_joint_creator: {
        type: "string",
        title: "Type of Joint Creator",
      },
      type_of_creator: {
        type: "string",
        title: "Type of Creator",
      },
      country_or_state_of_artwork_creation: {
        type: "string",
        title: "Country or State of Artwork Creation",
      },
      location_masterlist: {
        type: "string",
        title: "Location Masterlist",
      },
      list_of_artists: {
        type: "string",
        title: "List of Artists",
      },
      list_of_artists_alias: {
        type: "string",
        title: "List of Artists Alias",
      },
      essential_reading: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Essential Reading",
      },
      suggested_reading: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Suggested Reading",
      },
      highlight: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Highlight",
      },
      accession_number: {
        type: "string",
        title: "Accession Number",
      },
      personality_or_film_or_artwork_on_front_cover: {
        type: "string",
        title: "Personality, Film, or Artwork on Front Cover",
      },
      personality_or_film_or_artwork_on_front_cover_alias: {
        type: "string",
        title: "Personality, Film, or Artwork on Front Cover Alias",
      },
      publisher: {
        type: "string",
        title: "Publisher",
      },
      publisher_alias: {
        type: "string",
        title: "Publisher Alias",
      },
      object_alias: {
        type: "string",
        title: "Object Alias",
      },
      theme: {
        type: "string",
        title: "Theme",
      },
      contributors1_alias: {
        type: "string",
        title: "Contributors 1 Alias",
      },
      contributors1: {
        type: "string",
        title: "Contributors 1",
      },
      contributors1_type: {
        type: "string",
        title: "Contributors 1 Type",
      },
      contributors2_alias: {
        type: "string",
        title: "Contributors 2 Alias",
      },
      contributors2: {
        type: "string",
        title: "Contributors 2",
      },
      contributors2_type: {
        type: "string",
        title: "Contributors 2 Type",
      },
      language_data: {
        type: "string",
        title: "Language Data",
      },
      set_of: {
        type: "string",
        title: "Set Of",
      },
      copyright: {
        type: "string",
        title: "Copyright",
      },
      copyright_of_image: {
        type: "string",
        title: "Copyright of Image",
      },
      insight_number: {
        type: "string",
        title: "Insight Number",
      },
      insight_text_note1: {
        type: "string",
        title: "Insight Text Note 1",
      },
      insight_text_note2: {
        type: "string",
        title: "Insight Text Note 2",
      },
      insight_text_hyperlink: {
        type: "string",
        title: "Insight Text Hyperlink",
      },
      facial_recognition_derived_title_of_artwork: {
        type: "string",
        title: "Facial Recognition Derived Title of Artwork",
      },
      is_source: {
        type: "string",
        title: "Is Source",
        default: "",
      },
      search_text: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Search Text",
      },
      extended_tags: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Extended Tags",
      },
      rec_aliases: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Rec Aliases",
      },
      citation_quotation: {
        type: "string",
        title: "Citation Quotation",
      },
      quotations_and_other_text: {
        type: "string",
        title: "Quotations and Other Text",
      },
      age: {
        type: "string",
        title: "Age",
      },
    },
  };

  // Left column UI schema
  const leftColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    highlight: { "ui:field": HiddenField },
    agraphy_section_or_sub_section: { "ui:widget": "hidden" },
    agraphy_specific_section: { "ui:widget": "hidden" },
    type_of_joint_creator: { "ui:emptyValue": "" },
    type_of_creator: { "ui:emptyValue": "" },
    country_or_state_of_artwork_creation: { "ui:emptyValue": "" },
    location_masterlist: { "ui:widget": "hidden" },
    list_of_artists: { "ui:widget": "hidden" },
    list_of_artists_alias: { "ui:widget": "hidden" },
    accession_number: { "ui:emptyValue": "" },
    personality_or_film_or_artwork_on_front_cover: { "ui:emptyValue": "" },
    personality_or_film_or_artwork_on_front_cover_alias: {
      "ui:emptyValue": "",
    },
    publisher: { "ui:widget": "hidden" },
    publisher_alias: { "ui:widget": "hidden" },
    object_alias: { "ui:emptyValue": "" },
    quotations_and_other_text: {
      "ui:emptyValue": "",
      ...showWhen(leftFormData, "citation_quotation"),
    },
    theme: { "ui:emptyValue": "" },
    contributors1_alias: { "ui:emptyValue": "" },
    contributors1: {
      "ui:emptyValue": "",
      ...showWhen(leftFormData, "contributors1_alias"),
    },
    contributors1_type: {
      "ui:emptyValue": "",
      ...showWhen(leftFormData, "contributors1_alias"),
    },
    contributors2_alias: { "ui:emptyValue": "" },
    contributors2: {
      "ui:emptyValue": "",
      ...showWhen(leftFormData, "contributors2_alias"),
    },
    contributors2_type: {
      "ui:emptyValue": "",
      ...showWhen(leftFormData, "contributors2_alias"),
    },
    language_data: { "ui:emptyValue": "" },
    citation_quotation: { "ui:emptyValue": "" },
    age: { "ui:widget": "hidden" },
  };

  // Right column UI schema
  const rightColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    "ui:style": {
      fontSize: "14px",
    },
    is_source: { "ui:widget": "hidden" },
    search_text: { "ui:field": HiddenField },
    rec_aliases: { "ui:field": HiddenField },
    extended_tags: { "ui:field": HiddenField },
    set_of: { "ui:emptyValue": "" },
    copyright: { "ui:emptyValue": "" },
    copyright_of_image: { "ui:emptyValue": "" },
    insight_number: { "ui:emptyValue": "" },
    insight_text_note1: { "ui:emptyValue": "" },
    insight_text_note2: { "ui:emptyValue": "" },
    insight_text_hyperlink: { "ui:emptyValue": "" },
    facial_recognition_derived_title_of_artwork: { "ui:emptyValue": "" },
    essential_reading: {
      "ui:emptyValue": "",
    },
    suggested_reading: {
      "ui:emptyValue": "",
    },
  };

  // Combined UI schema for preview mode
  const combinedUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    is_source: { "ui:widget": "hidden" },
    search_text: { "ui:field": HiddenField },
    rec_aliases: { "ui:field": HiddenField },
    citation_quotation: {
      "ui:emptyValue": "",
    },
    copyright_of_image: {
      "ui:emptyValue": "",
    },
    age: { "ui:widget": "hidden" },
    extended_tags: { "ui:field": HiddenField },
    highlight: { "ui:field": HiddenField },
    essential_reading: {
      "ui:emptyValue": "",
    },
    suggested_reading: {
      "ui:emptyValue": "",
    },
    agraphy_section_or_sub_section: { "ui:widget": "hidden" },
    agraphy_specific_section: { "ui:widget": "hidden" },
    type_of_creator: { "ui:emptyValue": "" },
    country_or_state_of_artwork_creation: { "ui:emptyValue": "" },
    location_masterlist: { "ui:widget": "hidden" },
    list_of_artists: { "ui:widget": "hidden" },
    list_of_artists_alias: { "ui:widget": "hidden" },
    insight_number: { "ui:emptyValue": "" },
    insight_text_note1: { "ui:emptyValue": "" },
    insight_text_note2: { "ui:emptyValue": "" },
    insight_text_hyperlink: { "ui:emptyValue": "" },
    accession_number: { "ui:emptyValue": "" },
    personality_or_film_or_artwork_on_front_cover: { "ui:emptyValue": "" },
    personality_or_film_or_artwork_on_front_cover_alias: {
      "ui:emptyValue": "",
    },
    publisher: { "ui:widget": "hidden" },
    publisher_alias: { "ui:widget": "hidden" },
    object_alias: { "ui:emptyValue": "" },
    quotations_and_other_text: {
      "ui:emptyValue": "",
      ...showWhen(combinedFormData, "citation_quotation"),
    },
    theme: { "ui:emptyValue": "" },
    contributors1_alias: { "ui:emptyValue": "" },
    contributors1: {
      "ui:emptyValue": "",
      ...showWhen(combinedFormData, "contributors1_alias"),
    },
    contributors1_type: {
      "ui:emptyValue": "",
      ...showWhen(combinedFormData, "contributors1_alias"),
    },
    contributors2_alias: { "ui:emptyValue": "" },
    contributors2: {
      "ui:emptyValue": "",
      ...showWhen(combinedFormData, "contributors2_alias"),
    },
    contributors2_type: {
      "ui:emptyValue": "",
      ...showWhen(combinedFormData, "contributors2_alias"),
    },
    language_data: { "ui:emptyValue": "" },
    set_of: { "ui:emptyValue": "" },
    copyright: { "ui:emptyValue": "" },
    facial_recognition_derived_title_of_artwork: { "ui:emptyValue": "" },
  };

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getArchiveData(alias, "section-5");
          if (result) {
            const data = result?.data?.[0] || {};

            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(data);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(data);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data",
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      let finalFormData;

      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }

      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      if (alias) {
        const result = await updateArchiveData(alias, finalFormData);

        if (result?.status === 200) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">
                    Archive Quadrant-5 Fields
                  </Box>
                </HStack>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  fontsize={"14px"}
                />
              </Box>
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection="row"
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">
                        Archive Quadrant-5 Fields
                      </Box>
                    </HStack>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Section5;
